import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getCurrentUser } from '../services/userServices';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [decodedToken, setDecodedToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Logout: clear everything
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setDecodedToken(null);
    setUser(null);
  }, []);

  // Fetch user data using existing service
  const fetchUser = useCallback(async () => {
    try {
      const data = await getCurrentUser();
      // User data now includes permissions array from backend
      setUser(data);
      return data;
    } catch (error) {
      console.error('[AuthContext] Error fetching user:', error);
      logout();
      return null;
    }
  }, [logout]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser();
    }
  }, [token, fetchUser]);

  // Login: store token and decode it
  const login = useCallback(async (newToken) => {
    localStorage.setItem("token", newToken);
    const decoded = jwtDecode(newToken);
    setToken(newToken);
    setDecodedToken(decoded);
    // Fetch full user data with permissions
    await fetchUser();
  }, [fetchUser]);

  // Initialize token from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("token");
    
    if (stored) {
      try {
        const decoded = jwtDecode(stored);
        
        if (decoded.exp * 1000 > Date.now()) {
          setToken(stored);
          setDecodedToken(decoded);

          fetchUser().finally(() => setLoading(false));
        } else {
          localStorage.removeItem("token");
          setLoading(false);
        }
      } catch (e) {
        console.error('[AuthContext] Invalid token:', e);
        localStorage.removeItem("token");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  // Set global logout for API client
  useEffect(() => {
    window.logout = logout;
  }, [logout]);

  // Check if token is valid (exists and not expired)
  const isValid = useMemo(() => {
    const valid = decodedToken && decodedToken.exp * 1000 > Date.now();
    return valid;
  }, [decodedToken]);

  // Helper to check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  }, [user]);

  // Helper to check if user has any of the specified permissions
  const hasAnyPermission = useCallback((...permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.some(perm => user.permissions.includes(perm));
  }, [user]);

  // Helper to check if user has all specified permissions
  const hasAllPermissions = useCallback((...permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.every(perm => user.permissions.includes(perm));
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    token,
    decodedToken,
    user,
    login,
    logout,
    refreshUser,
    isValid,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  }), [token, decodedToken, user, login, logout, refreshUser, isValid, loading, hasPermission, hasAnyPermission, hasAllPermissions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper hooks for backward compatibility with UserContext pattern
export const useCurrentUser = () => {
  const { user } = useAuth();
  return user || null;
};

export const useRefreshUser = () => {
  const { refreshUser } = useAuth();
  return refreshUser || (() => {});
};